import {
  BookOutlined,
  ControlOutlined,
  EditOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Table,
} from "antd";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { FinanceAssetType, FinanceEntryType } from "../typeDefs";
import { AccountantAlignment } from "./AccountantAlignment";
import { AccountantExportPack } from "./AccountantExportPack";
import { AssetRegister } from "./AssetRegister";
import { BankCsvImporter } from "./BankCsvImporter";
import { BankBalanceReconciliation } from "./BankBalanceReconciliation";
import { BusinessTaxDashboard } from "./BusinessTaxDashboard";
import { DataIntegrityCheck } from "./DataIntegrityCheck";
import { FinanceAssetContainers } from "./FinanceAssetContainers";
import { FinanceFlowDiagram } from "./FinanceFlowDiagram";
import { FinanceGstReport } from "./FinanceGstReport";
import { Finances } from "./Finances";
import { FinanceOverview } from "./FinanceOverview";
import { PersonalTaxPlanner } from "./PersonalTaxPlanner";
import { PlannedExpenditure } from "./PlannedExpenditure";
import { PrintFinances } from "./PrintFinances";
import { ReconciliationRules } from "./ReconciliationRules";
import { TaxReviewViewer } from "./TaxReviewViewer";
import { TaxYearAdjustments } from "./TaxYearAdjustments";
import { getPersonalApportionmentAmount } from "./financeApportionment";
import { isInGstRegisteredPeriod } from "./taxConfig";
import "./financeStyles.css";

const salarySubCategories = ["Salaries and Wages", "Shareholder Salary"];

const getGstPortion = (amount: number) => (amount * 3) / 23;

const getReportGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;
  const gst = getGstPortion(entry.amount);
  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate = entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return gst * (apportionRate / 100);
  }
  return gst;
};

export const FinanceReports = () => {
  const nav = useNavigate();
  const [data, setData] = useState<Array<FinanceEntryType>>([]);
  const [financeAssets, setFinanceAssets] = useState<Array<FinanceAssetType>>([]);
  const [summaryData, setSummaryData] = useState<Array<any>>([]);
  const [activeItem, setActiveItem] = useState<FinanceEntryType | null>(null);
  const { RangePicker } = DatePicker;
  const [dateRange, setDateRange] = useState<Array<number>>([
    new Date(`${new Date().getFullYear()}-04-01`).getTime(),
    new Date().getTime(),
  ]);
  const [isModal, setIsModal] = useState(false);
  const [isImportModal, setIsImportModal] = useState(false);
  const [isGstModal, setIsGstModal] = useState(false);
  const [isBusinessTaxModal, setIsBusinessTaxModal] = useState(false);
  const [isIntegrityModal, setIsIntegrityModal] = useState(false);
  const [isAssetModal, setIsAssetModal] = useState(false);
  const [isAssetContainersModal, setIsAssetContainersModal] = useState(false);
  const [isPlanningModal, setIsPlanningModal] = useState(false);
  const [isOverviewModal, setIsOverviewModal] = useState(false);
  const [isExportModal, setIsExportModal] = useState(false);
  const [isRulesModal, setIsRulesModal] = useState(false);
  const [isPersonalTaxModal, setIsPersonalTaxModal] = useState(false);
  const [isTaxReviewViewerModal, setIsTaxReviewViewerModal] = useState(false);
  const [isTaxYearAdjustmentsModal, setIsTaxYearAdjustmentsModal] = useState(false);
  const [isBankReconciliationModal, setIsBankReconciliationModal] = useState(false);
  const [isAccountantAlignmentModal, setIsAccountantAlignmentModal] = useState(false);
  const [balance, setBalance] = useState({ cashAssets: 0, liabilities: 0 });

  const [balanceForm] = Form.useForm();

  const taxYearOptions = Array.from({ length: 8 }, (_item, idx) => {
    const endYear = new Date().getFullYear() + 2 - idx;
    return {
      value: `${new Date(`${endYear - 1}-04-01`).getTime()}_${new Date(`${endYear}-04-01`).getTime()}`,
      label: `Tax year ${endYear - 1}/${endYear}`,
    };
  });

  const selectTaxYear = (value: string) => {
    const [start, end] = value.split("_").map(Number);
    setDateRange([start, end]);
  };

  const processBalanceSheet = () => {
    balanceForm
      .validateFields()
      .then((values) => {
        setBalance(() => ({ ...values }));
      })
      .catch((err) => console.error(err));
  };

  const reassignToShareholderDraw = async (entry: FinanceEntryType) => {
    try {
      await updateDoc(doc(db, `finances/${entry.id}`), {
        category: "Equity",
        subCategory: "Shareholder Drawings",
        gstIncluded: false,
        gstRegistered: false,
        gstNumber: null,
        apportionMethod: null,
        apportionRate: 0,
        isPersonalCardSpend: true,
        yearEndTreatment: "Shareholder Salary",
        reclassifiedAsDrawAt: Date.now(),
        reclassifiedFromCategory: entry.category,
        reclassifiedFromSubCategory: entry.subCategory,
        reclassifiedFromGstIncluded: entry.gstIncluded,
        reclassifiedFromGstRegistered: entry.gstRegistered,
        reclassifiedFromGstNumber: entry.gstNumber || null,
        reconciliationConfidence: "manual",
      } as Partial<FinanceEntryType>);
      message.success("Reassigned to shareholder drawings for year-end salary treatment.");
    } catch (err) {
      console.error(err);
      message.error("Could not reassign spend to drawings.");
    }
  };

  const renderDrawAction = (entry: FinanceEntryType) =>
    entry.category === "Expenses" || entry.category === "Assets" ? (
      <Popconfirm
        title="Reassign to shareholder drawings?"
        description="This removes GST/business claim treatment and marks it as personal company-card spend to be considered for shareholder salary at year end."
        okText="Reassign"
        cancelText="Cancel"
        onConfirm={() => reassignToShareholderDraw(entry)}
      >
        <Button size="small">To Draw</Button>
      </Popconfirm>
    ) : null;

  const calculateStraightLineDepreciation = (
    purchaseDate: number,
    purchasePrice: number,
    depreciationRate: number
  ) => {
    console.log({ depreciationRate });
    const currentDate = new Date().getTime();
    const purchaseDateObj = purchaseDate;

    // Ensure the purchase date is valid and before the current date
    if (purchaseDateObj > currentDate) {
      throw new Error("Invalid purchase date.");
    }

    // Calculate the number of years since purchase
    const yearsSincePurchase =
      new Date(currentDate).getFullYear() -
      new Date(purchaseDateObj).getFullYear();

    if (purchasePrice < 1000) {
      return {
        yearsSincePurchase: yearsSincePurchase,
        annualDepreciation: 0,
        totalDepreciation: 0,
        currentValue: 0,
        writeOff: yearsSincePurchase < 1 ? true : false,
      };
    }

    // Depreciation per year
    const annualDepreciation = purchasePrice * (depreciationRate / 100);

    // Total depreciation to date
    const totalDepreciation = annualDepreciation * yearsSincePurchase;

    // Remaining value
    const currentValue = Math.max(0, purchasePrice - totalDepreciation);

    return {
      yearsSincePurchase,
      annualDepreciation,
      totalDepreciation,
      currentValue,
      writeOff: false,
    };
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "finances"), (snapshot) => {
      setData(
        snapshot.docs.map((snapDoc) => ({
          ...snapDoc.data(),
          id: snapDoc.id,
        }) as FinanceEntryType),
      );
    });

    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "financeAssets"), (snapshot) => {
      setFinanceAssets(
        snapshot.docs
          .map((snapDoc) => ({ id: snapDoc.id, ...(snapDoc.data() as Omit<FinanceAssetType, "id">) }))
          .filter((asset) => !asset.isDeleted)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      );
    });

    return unsub;
  }, []);

  useEffect(() => {
    setSummaryData([]);
    let gstOut = 0;
    let gstIn = 0;
    let gstBalance = 0;
    let expenses = 0;
    let revenue = 0;
    let expensesSal = 0;
    let purchases = 0; // gst balance
    let assetBookValue = 0;

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Revenue")
      .forEach((item) => {
        gstOut = gstOut + getReportGstAmount(item);
      });

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Assets" || item.category === "Expenses")
      .filter((item) => !salarySubCategories.includes(item.subCategory))
      .forEach((item) => {
        gstIn = gstIn + getReportGstAmount(item);
      });

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Expenses")
      .filter((item) => !salarySubCategories.includes(item.subCategory))
      .forEach((item) => {
        expenses = expenses + item.amount;
      });

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Expenses")
      .filter((item) => salarySubCategories.includes(item.subCategory))
      .forEach((item) => {
        expensesSal = expensesSal + item.amount;
      });

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Revenue")
      .forEach((item) => {
        revenue = revenue + item.amount;
      });

    data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1])
      .filter((item) => item.category === "Assets")
      .forEach((item) => {
        const calculatedDepreciation = calculateStraightLineDepreciation(
          item.date,
          item.amount,
          item.depreciationRate ? item.depreciationRate : 0
        );

        assetBookValue = assetBookValue + calculatedDepreciation.currentValue;
        purchases = purchases + item.amount;
      });

    gstBalance = gstIn - gstOut;
    let balanceResult =
      balance.cashAssets + assetBookValue - balance.liabilities;

    setSummaryData([
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>Total revenue</div>
            <Popover content="All money entering into the business (INCLUDES GST)">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$${revenue.toFixed(2)}`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>Expenses</div>
            <Popover content="Cost of expenses (not including salaries and asset purchases)">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$(${expenses.toFixed(2)})`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>Asset purchases</div>
            <Popover content="Cost of purchasing assets">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$(${purchases.toFixed(2)})`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>Salaries & Wages</div>
            <Popover content="Cost of salaries and wages">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$(${expensesSal.toFixed(2)})`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>
              Purchases + expenses + salaries
            </div>
            <Popover content="Cost of assets, expenses, personnel">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$(${(purchases + expenses + expensesSal).toFixed(2)})`,
      },

      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>GST to claim</div>
            <Popover content="GST on qualifing purchases (after GST registration 02/05/24)">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$${gstIn.toFixed(2)}`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>GST owing</div>
            <Popover content="GST on invoices sent (after GST registration 02/05/24)">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount: `$(${gstOut.toFixed(2)})`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>GST balance</div>
            <Popover content="GST to claim - GST owing">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount:
          gstBalance < 0
            ? `$(${Math.abs(gstBalance).toFixed(2)})`
            : `$${gstBalance.toFixed(2)}`,
      },
      {
        type: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "5px" }}>Equity</div>
            <Popover content="Assets - Liabilities = Equity">
              <InfoCircleOutlined style={{ color: "#5C6B73" }} />
            </Popover>
          </div>
        ),
        amount:
          balanceResult > 0
            ? `$${balanceResult.toFixed(2)}`
            : `$(${balanceResult.toFixed(2)})`,
      },
    ]);
  }, [data, dateRange, balance]);

  const summaryColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
  ];

  const columns = [
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Category",
      dataIndex: "subCategory",
      key: "subCategory",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
    },
    {
      title: "GST Number",
      dataIndex: "gstNumber",
      key: "gstNumber",
    },
    {
      title: "GST",
      dataIndex: "gst",
      key: "gst",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
  ];

  const expensesColumns = [
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Category",
      dataIndex: "subCategory",
      key: "subCategory",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
    },
    {
      title: "GST Number",
      dataIndex: "gstNumber",
      key: "gstNumber",
    },
    {
      title: "GST",
      dataIndex: "gst",
      key: "gst",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
  ];

  const assetsColumns = [
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Category",
      dataIndex: "subCategory",
      key: "subCategory",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
    },
    {
      title: "GST Number",
      dataIndex: "gstNumber",
      key: "gstNumber",
    },
    {
      title: "GST",
      dataIndex: "gst",
      key: "gst",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
  ];

  const calculateBookValue = (
    originalValue: number,
    purchaseDate: number,
    depreciationRate: number
  ) => {
    // Parse the purchase date and calculate the number of years since purchase
    const purchaseYear = new Date(purchaseDate).getFullYear();
    const currentYear = new Date().getFullYear();
    const yearsElapsed = currentYear - purchaseYear;

    // Calculate the annual depreciation amount
    const annualDepreciation = (originalValue * depreciationRate) / 100;

    // Calculate the total depreciation over the elapsed years
    const totalDepreciation = annualDepreciation * yearsElapsed;

    // Calculate the book value
    const bookValue = originalValue - totalDepreciation;

    if (originalValue < 1000) return 0;

    // Ensure the book value doesn't go below zero
    return Math.max(bookValue, 0);
  };

  const exportToCSV = (csvData: Array<any>) => {
    if (!csvData.length) {
      return "";
    }

    const headers = Object.keys(csvData[0]);
    const csvRows = [headers.join(",")];

    for (const row of csvData) {
      const values = headers.map((header: string) => {
        const value = row[header as keyof typeof row];
        // Escape quotes and handle commas inside values
        return typeof value === "string"
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  const downloadCSV = (csvdata: Array<any>) => {
    const csvData = exportToCSV(csvdata);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "data.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeEntries = data.filter((item) => !item.isDeleted);
  const periodEntries = activeEntries.filter(
    (item) => item.date >= dateRange[0] && item.date < dateRange[1],
  );
  const periodRevenue = periodEntries
    .filter((item) => item.category === "Revenue")
    .reduce((total, item) => total + item.amount, 0);
  const periodExpenses = periodEntries
    .filter((item) => item.category === "Expenses" && !item.financeAssetId)
    .reduce((total, item) => total + item.amount, 0);
  const periodAssets = periodEntries
    .filter((item) => item.category === "Assets" || item.financeAssetId)
    .reduce((total, item) => total + item.amount, 0);

  const visibleMainCategories = ["Revenue", "Expenses", "Assets", "Liabilities", "Equity", "Non-Business"];
  const getBaseDisplayRow = (item: FinanceEntryType, amountPrefix = "") => ({
    ...item,
    date: new Date(item.date).toLocaleDateString("en-GB"),
    amount: `${amountPrefix}$${item.amount.toFixed(2)}`,
    amountNumber: item.amount,
    gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : "-",
    key: item.id,
    edit: (
      <Button
        type="text"
        icon={
          <EditOutlined
            style={{ color: "#F6B34B" }}
            onClick={() => {
              setActiveItem(item);
              setIsModal(true);
            }}
          />
        }
      />
    ),
  });

  const getCategoryRows = (category: string, amountPrefix = "") =>
    periodEntries
      .filter((item) => item.category === category)
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((item) => getBaseDisplayRow(item, amountPrefix));

  const apportionedDrawRows = periodEntries
    .filter((item) => getPersonalApportionmentAmount(item) > 0)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((item) => ({
      ...getBaseDisplayRow(item),
      key: `${item.id}-personal-apportionment-draw`,
      category: "Equity",
      subCategory: "Shareholder Drawings",
      description: `${item.description || item.vendor} - personal portion of split/apportioned spend`,
      amount: `$${getPersonalApportionmentAmount(item).toFixed(2)}`,
      amountNumber: getPersonalApportionmentAmount(item),
      gst: "-",
    }));
  const equityRows = [...getCategoryRows("Equity"), ...apportionedDrawRows];

  const otherAccountRows = periodEntries
    .filter(
      (item) =>
        item.category === "Non-Business" ||
        [
          "Personal / Other Account",
          "Cash Not Banked",
          "Duplicate / Imported In Error",
          "Not Company Spend",
          "Exclude Pending Deletion",
        ].includes(item.subCategory),
    )
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((item) => getBaseDisplayRow(item, item.category === "Revenue" ? "" : ""));

  const uncategorisedRows = periodEntries
    .filter((item) => !visibleMainCategories.includes(item.category))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((item) => getBaseDisplayRow(item));

  const simpleTableFooter = (label: string) => (pageData: readonly any[]) => {
    const total = pageData.reduce((sum, item) => sum + (item.amountNumber || 0), 0);
    return (
      <div style={{ fontWeight: "bold", padding: "10px", textAlign: "right", width: "100%" }}>
        {`${label}: $${total.toFixed(2)} | Entries: ${pageData.length}`}
      </div>
    );
  };

  const buildAssetEntryRow = (item: FinanceEntryType, isChild = false) => {
    const calculatedDepreciation = calculateStraightLineDepreciation(
      item.date,
      item.amount,
      item.depreciationRate ? item.depreciationRate : 0,
    );

    return {
      ...item,
      rowKind: isChild ? "child" : "entry",
      date: new Date(item.date).toLocaleDateString("en-GB"),
      description: isChild ? `↳ ${item.description || item.vendor || "Finance entry"}` : item.description,
      amount: `$${item.amount.toFixed(2)}`,
      amountNumber: item.amount,
      rate:
        item.date < new Date("2024-05-02").getTime()
          ? "-"
          : `${item.apportionRate ? item.apportionRate : 0}%`,
      method: item.date < new Date("2024-05-02").getTime() ? "-" : item.apportionMethod,
      claim:
        item.date < new Date("2024-05-02").getTime()
          ? "-"
          : item.apportionRate && item.apportionMethod
          ? `$${getReportGstAmount(item).toFixed(2)}`
          : null,
      depreciationRate: item.amount < 1000 ? 0 : item.depreciationRate,
      depreciation:
        item.depreciationRate && calculatedDepreciation.totalDepreciation === 0
          ? "-"
          : `$${calculatedDepreciation.totalDepreciation}`,
      gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : null,
      key: isChild ? `${item.financeAssetId}-${item.id}` : item.id,
      bookValue:
        calculatedDepreciation.currentValue === 0
          ? "-"
          : item.depreciationRate && `$${calculatedDepreciation.currentValue.toFixed(2)}`,
      drawAction: renderDrawAction(item),
      edit: (
        <Button
          type="text"
          icon={
            <EditOutlined
              style={{ color: "#F6B34B" }}
              onClick={() => {
                setActiveItem(item);
                setIsModal(true);
              }}
            />
          }
        />
      ),
    };
  };

  const groupedAssetRows = financeAssets.flatMap((asset) => {
    const children = periodEntries
      .filter((item) => item.financeAssetId === asset.id)
      .sort((a, b) => (a.date < b.date ? -1 : 1));
    if (!children.length) return [];
    const total = children.reduce((sum, item) => sum + item.amount, 0);
    const gstTotal = children.reduce((sum, item) => sum + getReportGstAmount(item), 0);
    return [
      {
        key: `asset-container-${asset.id}`,
        rowKind: "parent",
        edit: "",
        date: "-",
        description: asset.name,
        subCategory: "Figurative Asset",
        vendor: "Asset Container",
        gstNumber: "-",
        method: "Grouped",
        rate: "-",
        claim: gstTotal ? `$${gstTotal.toFixed(2)}` : "-",
        amount: `$${total.toFixed(2)}`,
        amountNumber: total,
        depreciation: "-",
        bookValue: "-",
        drawAction: "",
      },
      ...children.map((item) => buildAssetEntryRow(item, true)),
    ];
  });

  const standaloneAssetRows = periodEntries
    .filter((item) => item.category === "Assets" && !item.financeAssetId)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((item) => buildAssetEntryRow(item));

  const assetTableRows = [...groupedAssetRows, ...standaloneAssetRows];

  const getAssetDownloadRows = () => {
    const rows: Record<string, unknown>[] = [];
    financeAssets.forEach((asset) => {
      const children = periodEntries
        .filter((item) => item.financeAssetId === asset.id)
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      if (!children.length) return;
      const total = children.reduce((sum, item) => sum + item.amount, 0);
      rows.push({
        Type: "Figurative Asset",
        Date: "",
        Description: asset.name,
        Category: "Figurative Asset",
        Vendor: "Asset Container",
        GST: children.reduce((sum, item) => sum + getReportGstAmount(item), 0).toFixed(2),
        Amount: total.toFixed(2),
      });
      children.forEach((item) => {
        rows.push({
          Type: "Child Entry",
          Date: new Date(item.date).toLocaleDateString("en-GB"),
          Description: item.description || "",
          Category: `${item.category} / ${item.subCategory}`,
          Vendor: item.vendor || "",
          GST: getReportGstAmount(item).toFixed(2),
          Amount: item.amount.toFixed(2),
        });
      });
      rows.push({ Type: "", Date: "", Description: "", Category: "", Vendor: "", GST: "", Amount: "" });
    });

    standaloneAssetRows.forEach((item) => {
      rows.push({
        Type: "Standalone Asset Entry",
        Date: item.date,
        Description: item.description || "",
        Category: item.subCategory || "",
        Vendor: item.vendor || "",
        GST: item.gst || "",
        Amount: item.amountNumber?.toFixed ? item.amountNumber.toFixed(2) : item.amount,
      });
    });

    return rows;
  };

  return (
    <div className="finance-shell">
      <div className="finance-main">
        <header className="finance-hero p-6 md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C6B73] animate-pulse">
                {"// OPS.FINANCE :: TAX_YEAR_LEDGER_ACTIVE"}
              </p>
              <h1 className="mt-5 font-oswald text-5xl uppercase leading-none tracking-tighter text-[#F6B34B] md:text-7xl">
                Finances
              </h1>
              <p className="mt-5 max-w-2xl border-l-2 border-[#F6B34B] pl-4 font-mono text-sm leading-relaxed text-gray-300 md:text-base">
                Manage business transactions, GST, assets, bank imports,
                shareholder drawings, and accountant-ready tax review outputs.
                <span className="mt-2 block text-[#5C6B73]">
                  {`${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(
                    dateRange[1],
                  ).toLocaleDateString("en-GB")} // ${periodEntries.length} entries`}
                </span>
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
                onClick={() => nav("/tools")}
                type="button"
              >
                Tools
              </button>
            </div>
          </div>
        </header>

        <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="finance-stat-card p-6">
            <p className="finance-kicker">Revenue</p>
            <div className="mt-3 font-oswald text-4xl text-[#F6B34B]">
              ${periodRevenue.toFixed(2)}
            </div>
          </div>
          <div className="finance-stat-card p-6">
            <p className="finance-kicker">Expenses</p>
            <div className="mt-3 font-oswald text-4xl text-white">
              ${periodExpenses.toFixed(2)}
            </div>
          </div>
          <div className="finance-stat-card p-6">
            <p className="finance-kicker">Assets</p>
            <div className="mt-3 font-oswald text-4xl text-white">
              ${periodAssets.toFixed(2)}
            </div>
          </div>
          <div className="finance-stat-card p-6">
            <p className="finance-kicker">Profit Est.</p>
            <div className="mt-3 font-oswald text-4xl text-white">
              ${(periodRevenue - periodExpenses).toFixed(2)}
            </div>
          </div>
        </section>

        <section className="finance-section mt-8">
          <div className="finance-section-title">
            <p className="finance-kicker">{"// finance.controls"}</p>
            <h2 className="finance-heading">Reporting Controls</h2>
            <div className="finance-accent" />
          </div>
          <div className="finance-section-body">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              placeholder="Select tax year"
              style={{ width: 190, marginRight: ".5rem" }}
              options={taxYearOptions}
              onChange={selectTaxYear}
            />
            <RangePicker
              value={undefined}
              onChange={(e) =>
                e &&
                setDateRange([
                  e[0]?.toDate().getTime() as number,
                  e[1]?.toDate().getTime() as number,
                ])
              }
            />
            <PrintFinances data={data} dates={dateRange} />
            <Popover content="Library of terms">
              <Button
                type="text"
                icon={<BookOutlined />}
                onClick={() => {
                  nav("/finance-library");
                }}
              />
            </Popover>
            <Button
              type="text"
              icon={<ControlOutlined />}
              onClick={() => nav("/tools")}
            />
          </div>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            <Button onClick={() => setIsOverviewModal(true)}>Overview</Button>
            <Button onClick={() => setIsGstModal(true)}>GST Report</Button>
            <Button onClick={() => setIsBusinessTaxModal(true)}>Tax Estimate</Button>
            <Button onClick={() => setIsIntegrityModal(true)}>Integrity Check</Button>
            <Button onClick={() => setIsAssetModal(true)}>Asset Register</Button>
            <Button onClick={() => setIsAssetContainersModal(true)}>Asset Containers</Button>
            <Button onClick={() => setIsPlanningModal(true)}>Plan Expenditure</Button>
            <Button onClick={() => setIsRulesModal(true)}>Reconciliation Rules</Button>
            <Button onClick={() => setIsPersonalTaxModal(true)}>Personal Tax</Button>
            <Button onClick={() => setIsTaxReviewViewerModal(true)}>AI Tax Reviews</Button>
            <Button onClick={() => setIsTaxYearAdjustmentsModal(true)}>Tax Carry-Overs</Button>
            <Button onClick={() => setIsBankReconciliationModal(true)}>Bank Reconciliation</Button>
            <Button onClick={() => setIsAccountantAlignmentModal(true)}>Accountant Alignment</Button>
            <Button onClick={() => setIsImportModal(true)}>Import Bank CSV</Button>
            <Button onClick={() => setIsExportModal(true)}>Export Pack</Button>
            <Button
              onClick={() => {
                setActiveItem(null);
                isModal ? setIsModal(false) : setIsModal(true);
              }}
            >
              New Entry
            </Button>
          </div>
        </div>
          </div>
        </section>
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  //padding: "10px",
                  textAlign: "left",
                  //width: "100%",
                  color: "#F6B34B",
                  fontSize: "1.5rem",
                  marginRight: "1rem",
                }}
              >
                {`${new Date(dateRange[0]).toLocaleDateString(
                  "en-GB"
                )} - ${new Date(dateRange[1]).toLocaleDateString(
                  "en-GB"
                )} Revenue`}
              </div>
              <Popover content="Total money flow into the business">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={data
            .filter((item) => !item.isDeleted)
            .filter(
              (item) => item.date >= dateRange[0] && item.date < dateRange[1]
            )
            .filter((item) => item.category === "Revenue")
            .sort((a, b) => (a.date < b.date ? -1 : 1))
            .map((item) => {
              return {
                ...item,
                date: new Date(item.date).toLocaleDateString("en-GB"),
                amount: `$${item.amount.toFixed(2)}`,
                amountNumber: item.amount,
                gstAmount: getReportGstAmount(item),
                gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : "-",
                key: item.id,
                edit: (
                  <Button
                    type="text"
                    icon={
                      <EditOutlined
                        style={{ color: "#F6B34B" }}
                        onClick={() => {
                          setActiveItem(item);
                          setIsModal(true);
                        }}
                      />
                    }
                  />
                ),
              };
            })}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={(pageData) => {
            let total = 0;
            let gstTotal = 0;

            pageData.forEach((item) => {
              total += item.amountNumber;
              gstTotal += item.gstAmount || 0;
            });
            return (
              <div
                style={{
                  fontWeight: "bold",
                  padding: "10px",
                  textAlign: "right",
                  width: "100%",
                }}
              >{`Total GST in: $${gstTotal.toFixed(
                2
              )}, Total revenue: $${total.toFixed(2)} | Entries: ${pageData.length}`}</div>
            );
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "1rem",
          }}
        >
          <div></div>
          <Button
            type="primary"
            onClick={() => {
              let csvdata = data
                .filter((item) => !item.isDeleted)
                .filter(
                  (item) =>
                    item.date >= dateRange[0] && item.date < dateRange[1]
                )
                .filter((item) => item.category === "Revenue")
                .sort((a, b) => (a.date < b.date ? -1 : 1))
                .map((item) => {
                  return {
                    ...item,
                    date: new Date(item.date).toLocaleDateString("en-GB"),
                    amount: `$${item.amount.toFixed(2)}`,
                    amountNumber: item.amount,
                    gstAmount: getReportGstAmount(item),
                    gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : "-",
                    key: item.id,
                    edit: (
                      <Button
                        type="text"
                        icon={
                          <EditOutlined
                            style={{ color: "#F6B34B" }}
                            onClick={() => {
                              setActiveItem(item);
                              setIsModal(true);
                            }}
                          />
                        }
                      />
                    ),
                  };
                });
              downloadCSV(csvdata);
            }}
          >
            Download Revenue
          </Button>
        </div>
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  //padding: "10px",
                  textAlign: "left",
                  //width: "100%",
                  color: "#F6B34B",
                  fontSize: "1.5rem",
                  marginRight: "1rem",
                }}
              >
                {`${new Date(dateRange[0]).toLocaleDateString(
                  "en-GB"
                )} - ${new Date(dateRange[1]).toLocaleDateString(
                  "en-GB"
                )} Assets`}
              </div>
              <Popover content="Assets held in the business based on purchases made by the business">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={[
            {
              title: "",
              dataIndex: "edit",
              key: "edit",
            },
            {
              title: "Date",
              dataIndex: "date",
              key: "date",
            },
            {
              title: "Description",
              dataIndex: "description",
              key: "description",
            },
            {
              title: "Category",
              dataIndex: "subCategory",
              key: "subCategory",
            },
            {
              title: "Vendor",
              dataIndex: "vendor",
              key: "vendor",
            },
            {
              title: "GST Number",
              dataIndex: "gstNumber",
              key: "gstNumber",
            },
            {
              title: "GST Method",
              dataIndex: "method",
              key: "method",
            },
            {
              title: "GST Rate",
              dataIndex: "rate",
              key: "rate",
            },
            {
              title: "GST Claim",
              dataIndex: "claim",
              key: "claim",
            },
            {
              title: "PurchaseValue",
              dataIndex: "amount",
              key: "amount",
            },
            {
              title: "Depreciation",
              dataIndex: "depreciation",
              key: "depreciation",
            },
            {
              title: "Book Value",
              dataIndex: "bookValue",
              key: "bk",
            },
            {
              title: "Personal spend",
              dataIndex: "drawAction",
              key: "drawAction",
            },
          ]}
          dataSource={assetTableRows}
          rowClassName={(record: any) =>
            record.rowKind === "parent"
              ? "bg-[#F6B34B]/10 font-bold"
              : record.rowKind === "child"
              ? "bg-white/[0.015]"
              : ""
          }
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={(pageData) => {
            const totalPurchase = pageData
              .filter((item: any) => item.rowKind !== "child")
              .reduce((sum: number, item: any) => sum + (item.amountNumber || 0), 0);
            const childCount = pageData.filter((item: any) => item.rowKind === "child" || item.rowKind === "entry").length;
            return (
              <div style={{ fontWeight: "bold", padding: "10px", textAlign: "right", width: "100%" }}>
                {`Total asset containers / purchases: $(${totalPurchase.toFixed(2)}) | Asset lines: ${childCount}`}
              </div>
            );
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "1rem",
          }}
        >
          <div></div>
          <Button
            type="primary"
            onClick={() => {
              downloadCSV(getAssetDownloadRows());
            }}
          >
            Download Assets
          </Button>
        </div>
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  //padding: "10px",
                  textAlign: "left",
                  //width: "100%",
                  color: "#F6B34B",
                  fontSize: "1.5rem",
                  marginRight: "1rem",
                }}
              >
                {`${new Date(dateRange[0]).toLocaleDateString(
                  "en-GB"
                )} - ${new Date(dateRange[1]).toLocaleDateString(
                  "en-GB"
                )} Expenses`}
              </div>
              <Popover content="Money flowing out of the business for services, subscriptions and other expenses (Excluding salaries and wages)">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={[
            {
              title: "",
              dataIndex: "edit",
              key: "edit",
            },
            {
              title: "Date",
              dataIndex: "date",
              key: "date",
            },
            {
              title: "Description",
              dataIndex: "description",
              key: "description",
            },
            {
              title: "Category",
              dataIndex: "subCategory",
              key: "subCategory",
            },
            {
              title: "Vendor",
              dataIndex: "vendor",
              key: "vendor",
            },
            {
              title: "GST Number",
              dataIndex: "gstNumber",
              key: "gstNumber",
            },
            {
              title: "GST Method",
              dataIndex: "method",
              key: "method",
            },
            {
              title: "GST Rate",
              dataIndex: "rate",
              key: "rate",
            },
            {
              title: "GST Claim",
              dataIndex: "claim",
              key: "claim",
            },
            {
              title: "Cost Value",
              dataIndex: "amount",
              key: "amount",
            },
            {
              title: "Personal spend",
              dataIndex: "drawAction",
              key: "drawAction",
            },
          ]}
          dataSource={data
            .filter((item) => !item.isDeleted)
            .filter(
              (item) => item.date >= dateRange[0] && item.date < dateRange[1]
            )
            .filter((item) => item.category === "Expenses")
            .filter((item) => !item.financeAssetId)
            .filter((item) => !salarySubCategories.includes(item.subCategory))
            .sort((a, b) => (a.date < b.date ? -1 : 1))
            .map((item) => {
              return {
                ...item,
                date: new Date(item.date).toLocaleDateString("en-GB"),
                amount: `$(${item.amount.toFixed(2)})`,
                amountNumber: item.amount,
                gstAmount: getReportGstAmount(item),
                rate:
                  item.date < new Date("2024-05-02").getTime()
                    ? "-"
                    : `${item.apportionRate ? item.apportionRate : 0}%`,
                method:
                  item.date < new Date("2024-05-02").getTime()
                    ? "-"
                    : item.apportionMethod,
                claim:
                  item.date < new Date("2024-05-02").getTime()
                    ? "-"
                    : item.apportionRate && item.apportionMethod
                    ? `$${getReportGstAmount(item).toFixed(2)}`
                    : null,
                gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : null,
                key: item.id,
                drawAction: renderDrawAction(item),
                edit: (
                  <Button
                    type="text"
                    icon={
                      <EditOutlined
                        style={{ color: "#F6B34B" }}
                        onClick={() => {
                          setActiveItem(item);
                          setIsModal(true);
                        }}
                      />
                    }
                  />
                ),
              };
            })}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={(pageData) => {
            let total = 0;

            pageData.forEach((item) => {
              total += item.amountNumber;
            });
            return (
              <div
                style={{
                  fontWeight: "bold",
                  padding: "10px",
                  textAlign: "right",
                  width: "100%",
                }}
              >{`Total cost of expenses: $(${total.toFixed(2)}) | Entries: ${pageData.length}`}</div>
            );
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "1rem",
          }}
        >
          <div></div>
          <Button
            type="primary"
            onClick={() => {
              let csvdata = data
                .filter((item) => !item.isDeleted)
                .filter(
                  (item) =>
                    item.date >= dateRange[0] && item.date < dateRange[1]
                )
                .filter((item) => item.category === "Expenses")
                .filter((item) => !item.financeAssetId)
                .filter((item) => !salarySubCategories.includes(item.subCategory))
                .sort((a, b) => (a.date < b.date ? -1 : 1))
                .map((item) => {
                  return {
                    ...item,
                    date: new Date(item.date).toLocaleDateString("en-GB"),
                    amount: `$(${item.amount.toFixed(2)})`,
                    amountNumber: item.amount,
                    gstAmount: getReportGstAmount(item),
                    rate:
                      item.date < new Date("2024-05-02").getTime()
                        ? "-"
                        : `${item.apportionRate ? item.apportionRate : 0}%`,
                    method:
                      item.date < new Date("2024-05-02").getTime()
                        ? "-"
                        : item.apportionMethod,
                    claim:
                      item.date < new Date("2024-05-02").getTime()
                        ? "-"
                        : item.apportionRate && item.apportionMethod
                        ? `$${getReportGstAmount(item).toFixed(2)}`
                        : null,
                    gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : null,
                    key: item.id,
                    edit: (
                      <Button
                        type="text"
                        icon={
                          <EditOutlined
                            style={{ color: "#F6B34B" }}
                            onClick={() => {
                              setActiveItem(item);
                              setIsModal(true);
                            }}
                          />
                        }
                      />
                    ),
                  };
                });
              downloadCSV(csvdata);
            }}
          >
            Download Expenses
          </Button>
        </div>
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  //padding: "10px",
                  textAlign: "left",
                  //width: "100%",
                  color: "#F6B34B",
                  fontSize: "1.5rem",
                  marginRight: "1rem",
                }}
              >
                {`${new Date(dateRange[0]).toLocaleDateString(
                  "en-GB"
                )} - ${new Date(dateRange[1]).toLocaleDateString(
                  "en-GB"
                )} Salaries & Wages`}
              </div>
              <Popover content="Money flowing out of the business to pay for staff wages and shareholder salaries">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={data
            .filter((item) => !item.isDeleted)
            .filter(
              (item) => item.date >= dateRange[0] && item.date < dateRange[1]
            )
            .filter(
              (item) =>
                item.category === "Expenses" &&
                salarySubCategories.includes(item.subCategory)
            )
            .sort((a, b) => (a.date < b.date ? -1 : 1))
            .map((item) => {
              return {
                ...item,
                date: new Date(item.date).toLocaleDateString("en-GB"),
                amount: `$(${item.amount.toFixed(2)})`,
                amountNumber: item.amount,
                gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : null,
                key: item.id,
                drawAction: renderDrawAction(item),
                edit: (
                  <Button
                    type="text"
                    icon={
                      <EditOutlined
                        style={{ color: "#F6B34B" }}
                        onClick={() => {
                          setActiveItem(item);
                          setIsModal(true);
                        }}
                      />
                    }
                  />
                ),
              };
            })}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={(pageData) => {
            let total = 0;

            pageData.forEach((item) => {
              total += item.amountNumber;
            });
            return (
              <div
                style={{
                  fontWeight: "bold",
                  padding: "10px",
                  textAlign: "right",
                  width: "100%",
                }}
              >{`Total cost of personnel: $(${total.toFixed(2)}) | Entries: ${pageData.length}`}</div>
            );
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "1rem",
          }}
        >
          <div></div>
          <Button
            type="primary"
            onClick={() => {
              let csvdata = data
                .filter((item) => !item.isDeleted)
                .filter(
                  (item) =>
                    item.date >= dateRange[0] && item.date < dateRange[1]
                )
                .filter(
                  (item) =>
                    item.category === "Expenses" &&
                    salarySubCategories.includes(item.subCategory)
                )
                .sort((a, b) => (a.date < b.date ? -1 : 1))
                .map((item) => {
                  return {
                    ...item,
                    date: new Date(item.date).toLocaleDateString("en-GB"),
                    amount: `$(${item.amount.toFixed(2)})`,
                    amountNumber: item.amount,
                    gst: getReportGstAmount(item) ? `$${getReportGstAmount(item).toFixed(2)}` : null,
                    key: item.id,
                    edit: (
                      <Button
                        type="text"
                        icon={
                          <EditOutlined
                            style={{ color: "#F6B34B" }}
                            onClick={() => {
                              setActiveItem(item);
                              setIsModal(true);
                            }}
                          />
                        }
                      />
                    ),
                  };
                });
              downloadCSV(csvdata);
            }}
          >
            Download Salaries
          </Button>
        </div>

        <Table
          title={() => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ fontWeight: "bold", color: "#F6B34B", fontSize: "1.5rem", marginRight: "1rem" }}>
                {`${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(dateRange[1]).toLocaleDateString("en-GB")} Equity / Drawings`}
              </div>
              <Popover content="Shareholder drawings, owner contributions, current account movements, and other equity entries.">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={equityRows}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={simpleTableFooter("Total equity movements")}
        />
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "1rem" }}>
          <div></div>
          <Button type="primary" onClick={() => downloadCSV(equityRows)}>Download Equity</Button>
        </div>

        <Table
          title={() => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ fontWeight: "bold", color: "#F6B34B", fontSize: "1.5rem", marginRight: "1rem" }}>
                {`${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(dateRange[1]).toLocaleDateString("en-GB")} Liabilities / Tax Payments`}
              </div>
              <Popover content="GST, income tax, PAYE, loans, accounts payable, and other liability entries.">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={getCategoryRows("Liabilities")}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={simpleTableFooter("Total liability movements")}
        />
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "1rem" }}>
          <div></div>
          <Button type="primary" onClick={() => downloadCSV(getCategoryRows("Liabilities"))}>Download Liabilities</Button>
        </div>

        <Table
          title={() => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ fontWeight: "bold", color: "#F6B34B", fontSize: "1.5rem", marginRight: "1rem" }}>
                {`${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(dateRange[1]).toLocaleDateString("en-GB")} Other Accounts / Non-Business`}
              </div>
              <Popover content="Entries imported from other accounts or not related to company finances. Move them here before deleting/excluding so every imported transaction remains visible during review.">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={otherAccountRows}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={simpleTableFooter("Total other-account/non-business entries")}
        />
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "1rem" }}>
          <div></div>
          <Button type="primary" onClick={() => downloadCSV(otherAccountRows)}>Download Other / Non-Business</Button>
        </div>

        <Table
          title={() => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ fontWeight: "bold", color: "#F6B34B", fontSize: "1.5rem", marginRight: "1rem" }}>
                {`${new Date(dateRange[0]).toLocaleDateString("en-GB")} - ${new Date(dateRange[1]).toLocaleDateString("en-GB")} Uncategorized / Unknown Category`}
              </div>
              <Popover content="Safety net for imported entries whose category is not recognised by the dashboard.">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={columns}
          dataSource={uncategorisedRows}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
          footer={simpleTableFooter("Total uncategorised/unknown entries")}
        />
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "1rem" }}>
          <div></div>
          <Button type="primary" onClick={() => downloadCSV(uncategorisedRows)}>Download Uncategorized</Button>
        </div>

        <FinanceFlowDiagram data={data} dateRange={dateRange} />

        <Form form={balanceForm} layout="inline">
          <Form.Item
            label="Total Liabilities"
            name="liabilities"
            rules={[{ required: true }, { type: "number" }]}
          >
            <InputNumber placeholder="Liabilities" />
          </Form.Item>
          <Form.Item
            label="Total Cash Assets"
            name="cashAssets"
            rules={[{ required: true }, { type: "number" }]}
          >
            <InputNumber placeholder="Cash Assets" />
          </Form.Item>
          <Button onClick={() => processBalanceSheet()}>
            <ReloadOutlined />
          </Button>
        </Form>
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  //padding: "10px",
                  textAlign: "left",
                  //width: "100%",
                  color: "#F6B34B",
                  fontSize: "1.5rem",
                  marginRight: "1rem",
                }}
              >
                {`${new Date(dateRange[0]).toLocaleDateString(
                  "en-GB"
                )} - ${new Date(dateRange[1]).toLocaleDateString(
                  "en-GB"
                )} Summary`}
              </div>
              <Popover content="Table of calculated summary data">
                <InfoCircleOutlined style={{ color: "#5C6B73" }} />
              </Popover>
            </div>
          )}
          columns={summaryColumns}
          dataSource={summaryData}
          size="small"
          style={{ width: "100%" }}
          pagination={false}
        />
      </div>
      <Modal
        className="finance-modal"
        open={isModal}
        onCancel={() => {
          setIsModal(false);
          setActiveItem(null);
        }}
        footer={null}
        destroyOnHidden
      >
        {isModal && <Finances activeItem={activeItem} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Import and reconcile bank CSV"
        open={isImportModal}
        onCancel={() => setIsImportModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isImportModal && <BankCsvImporter data={data} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Business finance overview"
        open={isOverviewModal}
        onCancel={() => setIsOverviewModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isOverviewModal && <FinanceOverview data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="GST payable / refundable report"
        open={isGstModal}
        onCancel={() => setIsGstModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isGstModal && <FinanceGstReport data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Business tax estimate dashboard"
        open={isBusinessTaxModal}
        onCancel={() => setIsBusinessTaxModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isBusinessTaxModal && <BusinessTaxDashboard data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Data integrity check"
        open={isIntegrityModal}
        onCancel={() => setIsIntegrityModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isIntegrityModal && <DataIntegrityCheck data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Asset register"
        open={isAssetModal}
        onCancel={() => setIsAssetModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isAssetModal && <AssetRegister data={data} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Figurative asset containers"
        open={isAssetContainersModal}
        onCancel={() => setIsAssetContainersModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isAssetContainersModal && <FinanceAssetContainers data={data} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Planned expenditure and capability growth"
        open={isPlanningModal}
        onCancel={() => setIsPlanningModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isPlanningModal && <PlannedExpenditure />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Reconciliation rules"
        open={isRulesModal}
        onCancel={() => setIsRulesModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isRulesModal && <ReconciliationRules />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Personal tax and drawings planner"
        open={isPersonalTaxModal}
        onCancel={() => setIsPersonalTaxModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isPersonalTaxModal && <PersonalTaxPlanner data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Accountant reporting alignment"
        open={isAccountantAlignmentModal}
        onCancel={() => setIsAccountantAlignmentModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isAccountantAlignmentModal && <AccountantAlignment data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Bank balance reconciliation"
        open={isBankReconciliationModal}
        onCancel={() => setIsBankReconciliationModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isBankReconciliationModal && <BankBalanceReconciliation data={data} dateRange={dateRange} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Tax year carry-overs and adjustments"
        open={isTaxYearAdjustmentsModal}
        onCancel={() => setIsTaxYearAdjustmentsModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isTaxYearAdjustmentsModal && <TaxYearAdjustments data={data} />}
      </Modal>
      <Modal
        className="finance-modal"
        title="AI tax recommendation reports"
        open={isTaxReviewViewerModal}
        onCancel={() => setIsTaxReviewViewerModal(false)}
        footer={null}
        width="95vw"
        destroyOnHidden
      >
        {isTaxReviewViewerModal && <TaxReviewViewer />}
      </Modal>
      <Modal
        className="finance-modal"
        title="Accountant export pack"
        open={isExportModal}
        onCancel={() => setIsExportModal(false)}
        footer={null}
        width="600px"
        destroyOnHidden
      >
        {isExportModal && <AccountantExportPack data={data} dateRange={dateRange} />}
      </Modal>
    </div>
  );
};
